import type { NexusReasonCode } from '@barmetvault/shared';
/**
 * Nexus Día — propuesta REAL del backend (POST /work/nexus/organize-day).
 *
 * Contrato real por propuesta (NexusDayProposal):
 *   { rank, taskId, taskCode, title, projectName, priority, status, taskType,
 *     dueDate, estimatedMinutes, proposedStart, proposedEnd,
 *     motive, blockers[], protectedActiveExecution }
 * Respuesta completa: { date, scheduleKnown, capacityMinutes, absenceMinutes,
 *   netCapacityMinutes, busy[], anchors[], proposals[], notPlaced[], totals }.
 *
 * NEXUS SOLO PROPONE: no reasigna ni modifica nada.
 */
import { useState } from 'react';
import { taskHierarchyLabel } from './tasks/taskTypes';
import { api } from '../utils/api';

interface NexusDayProposal {
  rank: number | null;
  taskId: string;
  taskCode: string;
  title: string;
  projectName: string;
  priority: string;
  status: string;
  dueDate: string | null;
  estimatedMinutes: number | null;
  proposedStart: string | null;
  proposedEnd: string | null;
  reasonCodes: NexusReasonCode[];
  motive: string;
  blockers: Array<{ code?: string; detail?: string; capability?: string } | string>;
  protectedActiveExecution: boolean;
}

interface OrganizeDayResponse {
  date: string;
  scheduleKnown: boolean;
  capacityMinutes: number;
  absenceMinutes: number;
  netCapacityMinutes: number;
  busy: Array<{ kind: string; start: string; end: string; label: string }>;
  proposals: NexusDayProposal[];
  notPlaced: NexusDayProposal[];
  totals: { openTasks: number; placed: number; notPlaced: number; proposedMinutes: number; freeMinutes: number };
}

function blockerText(b: NexusDayProposal['blockers'][number]): string {
  return typeof b === 'string' ? b : (b.detail || b.capability || b.code || JSON.stringify(b));
}

export function NexusDayPanel() {
  const [date, setDate] = useState('');
  const [data, setData] = useState<OrganizeDayResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/work/nexus/organize-day', date ? { date } : {});
      setData(res.data);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err?.response?.data?.message || err?.message || 'No se pudo generar la propuesta del día');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="nexus-day-panel" data-testid="nexus-day-panel">
      <h2>Nexus · Día</h2>
      <div>
        <label htmlFor="nexus-day-date">Fecha (opcional)</label>
        <input
          id="nexus-day-date" type="date" data-testid="nexus-day-date"
          value={date} onChange={(e) => setDate(e.target.value)}
        />
        <button data-testid="nexus-day-run" onClick={() => void run()} disabled={loading}>
          {loading ? 'Generando propuesta…' : 'Generar propuesta del día'}
        </button>
      </div>

      {loading && <div data-testid="nexus-day-loading" role="status">Generando propuesta del día…</div>}
      {error && <div data-testid="nexus-day-error" role="alert">{error}</div>}

      {data && !loading && (
        <div data-testid="nexus-day-result">
          <section data-testid="nexus-day-capacity">
            <h3>Capacidad · {data.date}</h3>
            <p>
              Capacidad teórica: {data.capacityMinutes} min · Ausencias: {data.absenceMinutes} min ·
              Neta: <strong>{data.netCapacityMinutes} min</strong>
              {!data.scheduleKnown && <span> (horario desconocido: no se inventa capacidad)</span>}
            </p>
            {data.busy.length > 0 && (
              <ul data-testid="nexus-day-busy">
                {data.busy.map((b) => (
                  <li key={`${b.start}-${b.end}`}>{b.kind}: {b.start} → {b.end} {b.label}</li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3>Propuestas ({data.proposals.length})</h3>
            <ul>
              {data.proposals.map((p) => (
                <li key={p.taskId} data-testid="nexus-day-proposal">
                  <div>
                    {taskHierarchyLabel({ projectName: p.projectName, taskCode: p.taskCode, title: p.title })}
                    {p.rank !== null && <span> · #{p.rank}</span>}
                  </div>
                  <div>
                    {p.proposedStart && (
                      <span data-testid="nexus-proposal-slot">
                        {p.proposedStart} → {p.proposedEnd}
                      </span>
                    )}
                  </div>
                  <div data-testid="nexus-proposal-motive">Motivo: {p.motive}</div>
                  {p.blockers.length > 0 && (
                    <div data-testid="nexus-proposal-blockers">
                      Bloqueos: {p.blockers.map(blockerText).join(' · ')}
                    </div>
                  )}
                  {p.protectedActiveExecution && (
                    <div data-testid="nexus-proposal-protected">
                      Ejecución activa protegida: no se reordena
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>

          {data.notPlaced.length > 0 && (
            <section>
              <h3>No colocadas ({data.notPlaced.length})</h3>
              <ul>
                {data.notPlaced.map((p) => (
                  <li key={p.taskId} data-testid="nexus-day-not-placed">
                    {taskHierarchyLabel({ taskCode: p.taskCode, title: p.title })}: {p.motive}
                    {p.blockers.length > 0 && ` · ${p.blockers.map(blockerText).join(' · ')}`}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section data-testid="nexus-day-totals">
            <p>
              Tareas abiertas: {data.totals.openTasks} · Colocadas: {data.totals.placed} ·
              No colocadas: {data.totals.notPlaced} · Propuestas: {data.totals.proposedMinutes} min ·
              Libres: {data.totals.freeMinutes} min
            </p>
            <p className="hint">Nexus propone: no reasigna ni modifica tareas automáticamente.</p>
          </section>
        </div>
      )}
    </div>
  );
}
